// Format helpers. No date library — DESIGN.md §3 constraint for the POC.

/**
 * Format an ISO-ish timestamp as `YYYY-MM-DD HH:mm UTC`. Returns the input
 * verbatim if parsing fails, and the placeholder "—" if input is empty or
 * represents epoch zero (1970-01-01T00:00:00Z).
 */
export function formatUtcMinute(input: string | null | undefined): string {
  if (input === null || input === undefined || input === "") return "—";
  try {
    const date = new Date(input);
    if (Number.isNaN(date.getTime())) return input;
    // Treat epoch zero as "no data" — system roles and unset timestamps
    // often come through as 0 / "1970-01-01T00:00:00Z".
    if (date.getTime() === 0) return "—";
    const iso = date.toISOString(); // e.g. 2026-04-24T12:34:56.789Z
    return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
  } catch {
    return input;
  }
}

/**
 * Return a short "MM:SS" or "Hh MMm" remaining string for a future
 * timestamp; negative durations return "0s".
 */
export function formatRemaining(targetIso: string | null | undefined, nowMs: number): string {
  if (!targetIso) return "—";
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return "—";
  const delta = Math.max(0, target - nowMs);
  const seconds = Math.floor(delta / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (delta === 0) return "expired";
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

/** Returns a tone hint for the session countdown per DESIGN.md §8.2. */
export function remainingTone(targetIso: string | null | undefined, nowMs: number): "info" | "warning" | "danger" {
  if (!targetIso) return "info";
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return "info";
  const delta = target - nowMs;
  if (delta <= 0) return "danger";
  if (delta < 5 * 60 * 1000) return "warning";
  return "info";
}

/**
 * Human-friendly relative timestamp (e.g. "2 min ago", "3h ago", "yesterday",
 * "Mar 3"). Used by the Access landing page's "Modified" column. `now`
 * defaults to `Date.now()` but is overridable for deterministic tests.
 */
export function formatRelativeTime(
  input: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!input) return "—";
  const ts = new Date(input).getTime();
  if (Number.isNaN(ts) || ts === 0) return "—";
  const deltaSec = Math.floor((now - ts) / 1000);

  // Future timestamps are unusual in this UI but possible on drifted clocks.
  // Show them as "just now" so we don't accidentally render "-3 min ago".
  if (deltaSec < 30) return "just now";
  if (deltaSec < 60) return `${deltaSec}s ago`;

  const minutes = Math.floor(deltaSec / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;

  // Older than a week — fall back to a compact date (e.g. "Mar 3" / "Mar 3, 2024").
  const date = new Date(ts);
  const currentYear = new Date(now).getFullYear();
  const sameYear = date.getFullYear() === currentYear;
  const month = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  const day = date.getUTCDate();
  return sameYear ? `${month} ${day}` : `${month} ${day}, ${date.getUTCFullYear()}`;
}
